<template>
  <div class="login-page">
    <!-- Sfondo: globo delle rotte marittime -->
    <div class="globe-bg" aria-hidden="true">
      <canvas ref="canvas" class="globe-canvas"></canvas>
      <div class="atmo"></div>
      <div class="haze"></div>
    </div>

    <!-- Card di login, centrata -->
    <div class="login-content">
      <div class="login-card">
        <div class="login-brand">
          <img src="/logo.png" alt="Lex System" class="login-logo" />
          <p class="login-subtitle">{{ $t('login.subtitle') }}</p>
        </div>

        <v-form class="login-form" @submit.prevent="login">
          <v-text-field
            v-model="credentials.username"
            :label="$t('login.username')"
            name="username"
            type="text"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            color="primary"
            prepend-inner-icon="mdi-account-outline"
            autocomplete="username"
            autofocus
            required
          />

          <v-text-field
            v-model="credentials.password"
            :label="$t('login.password')"
            name="password"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            color="primary"
            prepend-inner-icon="mdi-lock-outline"
            :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
            autocomplete="current-password"
            required
            @click:append-inner="showPassword = !showPassword"
          />

          <v-expand-transition>
            <v-alert
              v-if="error"
              type="error"
              variant="tonal"
              density="compact"
              rounded="lg"
              class="mb-4"
            >
              {{ error }}
            </v-alert>
          </v-expand-transition>

          <v-btn
            class="login-btn"
            type="submit"
            size="large"
            block
            rounded="lg"
            :loading="loading"
          >
            <v-icon start>mdi-login-variant</v-icon>
            {{ loading ? $t('login.loading') : $t('login.loginButton') }}
          </v-btn>
        </v-form>

        <div class="login-footer">
          <span>v{{ appVersion }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../stores/auth.js'

const RAD = Math.PI / 180
const TILT = 22 * RAD          // inclinazione dell'asse, da vista orbitale
const SPIN_PERIOD = 190        // secondi per un giro completo del pianeta
// Sole fisso in alto a destra, lontano dal globo: direzione unitaria in spazio vista
const SUN = { x: 0.366, y: 0.512, z: 0.777 }
const SUN_DISTANCE = 2.45      // distanza del sole dal centro, in raggi
const MERIDIANS = 14
const PARALLELS = 7

// Porti reali: la rete di rotte nasce da qui
const PORTS = [
  { name: 'Santos', lat: -23.9, lon: -46.3 },
  { name: 'Rotterdam', lat: 51.9, lon: 4.5 },
  { name: 'Shanghai', lat: 31.2, lon: 121.5 },
  { name: 'Singapore', lat: 1.3, lon: 103.8 },
  { name: 'Los Angeles', lat: 33.7, lon: -118.3 },
  { name: 'Amburgo', lat: 53.5, lon: 9.9 },
  { name: 'Jebel Ali', lat: 25.0, lon: 55.1 },
  { name: 'Durban', lat: -29.9, lon: 31.0 },
  { name: 'New York', lat: 40.7, lon: -74.0 },
  { name: 'Busan', lat: 35.1, lon: 129.0 }
]

// Coppie collegate da una rotta
const ROUTES = [
  [0, 1], [0, 8], [2, 3], [3, 6], [1, 6],
  [4, 9], [2, 4], [7, 3], [8, 5], [7, 0]
]

export default {
  name: 'Login',
  data() {
    return {
      credentials: {
        username: '',
        password: ''
      },
      showPassword: false,
      loading: false,
      error: null,
      appVersion: __APP_VERSION__
    }
  },
  mounted() {
    // Stato del disegno fuori da data(): non serve reattivita
    this.ctx = null
    this.rafId = null
    this.dpr = 1
    this.width = 0
    this.height = 0
    this.stars = []
    this.routes = []
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this.initCanvas()
    window.addEventListener('resize', this.onResize)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeUnmount() {
    this.stopLoop()
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    // ---------- Preparazione ----------
    initCanvas() {
      const canvas = this.$refs.canvas
      if (!canvas) return

      this.ctx = canvas.getContext('2d')
      this.resizeCanvas()
      this.buildStars()
      this.buildRoutes()

      if (this.reducedMotion) {
        this.draw(0) // fotogramma singolo
      } else {
        this.startLoop()
      }
    },

    resizeCanvas() {
      const canvas = this.$refs.canvas
      if (!canvas || !this.ctx) return

      this.dpr = Math.min(window.devicePixelRatio || 1, 2)
      this.width = canvas.clientWidth
      this.height = canvas.clientHeight
      canvas.width = Math.round(this.width * this.dpr)
      canvas.height = Math.round(this.height * this.dpr)
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)

      // Il globo sta in basso, dietro la card: il disco resta quasi tutto in vista
      const min = Math.min(this.width, this.height)
      const compact = this.width < 900
      this.radius = min * (compact ? 0.78 : 0.62)
      this.cx = this.width * 0.5
      this.cy = this.height * (compact ? 1.02 : 0.95)
      // su schermi stretti il sole va avvicinato, altrimenti finisce fuori campo
      this.sunDistance = compact ? 1.35 : SUN_DISTANCE
    },

    buildStars() {
      const count = Math.round((this.width * this.height) / 14000)
      this.stars = Array.from({ length: Math.min(140, count) }, () => ({
        x: Math.random() * this.width,
        y: Math.random() * this.height * 0.75,
        r: 0.4 + Math.random() * 1.1,
        phase: Math.random() * Math.PI * 2,
        speed: 0.3 + Math.random() * 0.9
      }))
    },

    buildRoutes() {
      this.routes = ROUTES.map(([a, b], i) => ({
        a: this.toVector(PORTS[a].lat, PORTS[a].lon),
        b: this.toVector(PORTS[b].lat, PORTS[b].lon),
        // ogni spedizione viaggia con tempi propri
        speed: 0.035 + Math.random() * 0.05,
        offset: Math.random(),
        gold: i % 3 === 0
      }))
    },

    // ---------- Geometria sferica ----------
    toVector(lat, lon) {
      const la = lat * RAD
      const lo = lon * RAD
      return {
        x: Math.cos(la) * Math.cos(lo),
        y: Math.sin(la),
        z: Math.cos(la) * Math.sin(lo)
      }
    },

    // Da vettore in spazio vista a coordinate schermo
    toScreen(v, altitude = 1) {
      const r = this.radius * altitude
      return { x: this.cx + v.x * r, y: this.cy - v.y * r }
    },

    // Rotazione attorno all'asse polare + inclinazione dell'osservatore
    project(v, spin, altitude = 1) {
      const cs = Math.cos(spin)
      const sn = Math.sin(spin)
      const x1 = v.x * cs - v.z * sn
      const z1 = v.x * sn + v.z * cs

      const ct = Math.cos(TILT)
      const st = Math.sin(TILT)
      const y2 = v.y * ct - z1 * st
      const z2 = v.y * st + z1 * ct

      const r = this.radius * altitude
      return {
        x: this.cx + x1 * r,
        y: this.cy - y2 * r,
        z: z2 // > 0 = rivolto verso l'osservatore
      }
    },

    // Interpolazione sul grande cerchio tra due punti
    slerp(a, b, t) {
      const dot = Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z))
      const omega = Math.acos(dot)
      if (omega < 1e-4) return a

      const s = Math.sin(omega)
      const k1 = Math.sin((1 - t) * omega) / s
      const k2 = Math.sin(t * omega) / s
      return {
        x: a.x * k1 + b.x * k2,
        y: a.y * k1 + b.y * k2,
        z: a.z * k1 + b.z * k2
      }
    },

    // ---------- Ciclo di disegno ----------
    startLoop() {
      if (this.rafId) return
      const step = (time) => {
        this.draw(time)
        this.rafId = requestAnimationFrame(step)
      }
      this.rafId = requestAnimationFrame(step)
    },

    stopLoop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
    },

    draw(time) {
      const ctx = this.ctx
      if (!ctx) return

      const t = time / 1000
      const spin = (t / SPIN_PERIOD) * Math.PI * 2

      const sun = SUN

      ctx.clearRect(0, 0, this.width, this.height)
      this.drawStars(ctx, t)
      this.drawSphere(ctx)
      this.drawDayNight(ctx, sun)
      this.drawGrid(ctx, spin, sun)
      this.drawRoutes(ctx, spin, t, sun)
      this.drawGlint(ctx, sun)
      this.drawRim(ctx, sun)
      this.drawSun(ctx, sun, t)
      this.drawFlare(ctx, sun)
    },

    // Disco solare con alone: fermo, piccolo, lontano
    drawSun(ctx, sun, t) {
      const pos = this.toScreen(sun, this.sunDistance)
      // respiro appena percettibile dell'alone
      const depth = 0.94 + 0.06 * Math.sin(t * 0.5)
      const core = this.radius * 0.028
      const halo = this.radius * 0.34

      ctx.save()
      ctx.globalCompositeOperation = 'lighter'

      const glow = ctx.createRadialGradient(pos.x, pos.y, core * 0.4, pos.x, pos.y, halo)
      glow.addColorStop(0, `rgba(255, 236, 190, ${(0.85 * depth).toFixed(3)})`)
      glow.addColorStop(0.18, `rgba(255, 196, 92, ${(0.35 * depth).toFixed(3)})`)
      glow.addColorStop(0.5, `rgba(245, 150, 40, ${(0.10 * depth).toFixed(3)})`)
      glow.addColorStop(1, 'rgba(245, 140, 30, 0)')
      ctx.fillStyle = glow
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, halo, 0, Math.PI * 2)
      ctx.fill()

      ctx.fillStyle = `rgba(255, 246, 220, ${(0.95 * depth).toFixed(3)})`
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, core, 0, Math.PI * 2)
      ctx.fill()

      ctx.restore()
    },

    // Giorno e notte: la luce cade dal punto sub-solare
    drawDayNight(ctx, sun) {
      const sub = this.toScreen(sun)

      ctx.save()
      ctx.beginPath()
      ctx.arc(this.cx, this.cy, this.radius, 0, Math.PI * 2)
      ctx.clip()

      const lit = ctx.createRadialGradient(
        sub.x, sub.y, this.radius * 0.05,
        sub.x, sub.y, this.radius * 1.15
      )
      lit.addColorStop(0, 'rgba(90, 150, 210, 0.34)')
      lit.addColorStop(0.35, 'rgba(50, 100, 165, 0.16)')
      lit.addColorStop(0.72, 'rgba(10, 30, 60, 0)')
      ctx.fillStyle = lit
      ctx.fillRect(this.cx - this.radius, this.cy - this.radius, this.radius * 2, this.radius * 2)

      // lato in ombra
      const dark = ctx.createRadialGradient(
        this.cx - (sub.x - this.cx), this.cy - (sub.y - this.cy), this.radius * 0.1,
        this.cx - (sub.x - this.cx), this.cy - (sub.y - this.cy), this.radius * 1.25
      )
      dark.addColorStop(0, 'rgba(2, 6, 14, 0.55)')
      dark.addColorStop(0.6, 'rgba(2, 6, 14, 0.18)')
      dark.addColorStop(1, 'rgba(2, 6, 14, 0)')
      ctx.fillStyle = dark
      ctx.fillRect(this.cx - this.radius, this.cy - this.radius, this.radius * 2, this.radius * 2)

      ctx.restore()
    },

    // Riflesso speculare sull'oceano, dove il sole rimbalza verso l'osservatore
    drawGlint(ctx, sun) {
      // vettore mediano tra luce e osservatore
      const hx = sun.x
      const hy = sun.y
      const hz = sun.z + 1
      const len = Math.hypot(hx, hy, hz)
      const h = { x: hx / len, y: hy / len, z: hz / len }
      if (h.z <= 0) return

      const pos = this.toScreen(h)
      const size = this.radius * 0.3

      ctx.save()
      ctx.beginPath()
      ctx.arc(this.cx, this.cy, this.radius, 0, Math.PI * 2)
      ctx.clip()
      ctx.globalCompositeOperation = 'lighter'

      const g = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, size)
      const strength = Math.pow(h.z, 3)
      g.addColorStop(0, `rgba(255, 240, 205, ${(0.5 * strength).toFixed(3)})`)
      g.addColorStop(0.25, `rgba(255, 205, 120, ${(0.18 * strength).toFixed(3)})`)
      g.addColorStop(1, 'rgba(255, 190, 90, 0)')
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, size, 0, Math.PI * 2)
      ctx.fill()

      ctx.restore()
    },

    // Bordo atmosferico piu acceso dal lato del sole
    drawRim(ctx, sun) {
      const from = this.toScreen(sun, 1.05)
      const to = this.toScreen({ x: -sun.x, y: -sun.y, z: -sun.z }, 1.05)

      const grad = ctx.createLinearGradient(from.x, from.y, to.x, to.y)
      grad.addColorStop(0, 'rgba(190, 220, 255, 0.55)')
      grad.addColorStop(0.45, 'rgba(120, 175, 255, 0.18)')
      grad.addColorStop(1, 'rgba(60, 110, 190, 0.05)')

      ctx.save()
      ctx.globalCompositeOperation = 'lighter'
      ctx.strokeStyle = grad
      ctx.lineWidth = 2.5
      ctx.beginPath()
      ctx.arc(this.cx, this.cy, this.radius, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    },

    // Bagliore di lente: dischi allineati tra sole e centro schermo
    drawFlare(ctx, sun) {
      const pos = this.toScreen(sun, this.sunDistance)
      const midX = this.width / 2
      const midY = this.height / 2

      ctx.save()
      ctx.globalCompositeOperation = 'lighter'

      const ghosts = [
        { t: 0.32, r: 0.05, a: 0.10, c: '255, 210, 140' },
        { t: 0.58, r: 0.028, a: 0.08, c: '150, 200, 255' },
        { t: 0.85, r: 0.07, a: 0.06, c: '255, 180, 110' },
        { t: 1.25, r: 0.035, a: 0.05, c: '255, 235, 190' }
      ]

      for (const g of ghosts) {
        const x = pos.x + (midX - pos.x) * g.t * 2
        const y = pos.y + (midY - pos.y) * g.t * 2
        const rad = this.radius * g.r
        const grad = ctx.createRadialGradient(x, y, 0, x, y, rad)
        grad.addColorStop(0, `rgba(${g.c}, ${g.a})`)
        grad.addColorStop(1, `rgba(${g.c}, 0)`)
        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(x, y, rad, 0, Math.PI * 2)
        ctx.fill()
      }

      ctx.restore()
    },

    drawStars(ctx, t) {
      for (const s of this.stars) {
        const alpha = 0.15 + 0.5 * (0.5 + 0.5 * Math.sin(t * s.speed + s.phase))
        ctx.fillStyle = `rgba(200, 220, 255, ${alpha.toFixed(3)})`
        ctx.beginPath()
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
        ctx.fill()
      }
    },

    // Corpo del pianeta: disco scuro con bordo illuminato
    drawSphere(ctx) {
      const grad = ctx.createRadialGradient(
        this.cx, this.cy - this.radius * 0.35, this.radius * 0.1,
        this.cx, this.cy, this.radius
      )
      grad.addColorStop(0, '#0e2036')
      grad.addColorStop(0.65, '#091629')
      grad.addColorStop(1, '#050c18')

      ctx.fillStyle = grad
      ctx.beginPath()
      ctx.arc(this.cx, this.cy, this.radius, 0, Math.PI * 2)
      ctx.fill()

      // Alone atmosferico sul limbo
      ctx.strokeStyle = 'rgba(120, 175, 255, 0.35)'
      ctx.lineWidth = 2
      ctx.stroke()
    },

    // Quanto un punto della sfera e illuminato (0 = notte, 1 = sole a picco)
    lambert(p, sun, altitude = 1) {
      const nx = (p.x - this.cx) / (this.radius * altitude)
      const ny = (this.cy - p.y) / (this.radius * altitude)
      return Math.max(0, nx * sun.x + ny * sun.y + p.z * sun.z)
    },

    // Meridiani e paralleli
    drawGrid(ctx, spin, sun) {
      ctx.lineWidth = 1

      for (let m = 0; m < MERIDIANS; m++) {
        const lon = (m / MERIDIANS) * 360
        this.strokeArc(ctx, (i, steps) => {
          const lat = -90 + (180 * i) / steps
          return this.toVector(lat, lon)
        }, spin, sun, '110, 165, 235')
      }

      for (let p = 1; p < PARALLELS; p++) {
        const lat = -90 + (180 * p) / PARALLELS
        this.strokeArc(ctx, (i, steps) => {
          const lon = (360 * i) / steps
          return this.toVector(lat, lon)
        }, spin, sun, '110, 165, 235')
      }
    },

    // Traccia una curva sferica saltando i tratti sul lato nascosto.
    // Ogni tratto e disegnato a se: cosi la griglia si accende sotto il sole.
    strokeArc(ctx, pointAt, spin, sun, tone) {
      const steps = 48
      let prev = null
      let prevAlpha = -1

      for (let i = 0; i <= steps; i++) {
        const p = this.project(pointAt(i, steps), spin)
        const visible = p.z > 0 && p.y < this.height + 40

        if (!visible) {
          if (prevAlpha >= 0) ctx.stroke()
          prev = null
          prevAlpha = -1
          continue
        }

        const lam = this.lambert(p, sun)
        // sfuma verso il limbo e si spegne sul lato in ombra;
        // l'alpha e quantizzata per accorpare i segmenti in un solo tracciato
        const alpha = Math.round((0.035 + p.z * 0.07 + lam * 0.20) * 60) / 60

        if (prev) {
          if (alpha !== prevAlpha) {
            if (prevAlpha >= 0) ctx.stroke()
            ctx.strokeStyle = `rgba(${tone}, ${alpha.toFixed(3)})`
            ctx.beginPath()
            ctx.moveTo(prev.x, prev.y)
            prevAlpha = alpha
          }
          ctx.lineTo(p.x, p.y)
        }

        prev = p
      }

      if (prevAlpha >= 0) ctx.stroke()
    },

    // Rotte, spedizioni in viaggio e porti
    drawRoutes(ctx, spin, t, sun) {
      const steps = 48

      for (const route of this.routes) {
        const tone = route.gold ? '245, 179, 1' : '130, 190, 255'

        // arco della rotta, rialzato sulla sfera
        ctx.lineWidth = 1.2
        let drawing = false
        for (let i = 0; i <= steps; i++) {
          const f = i / steps
          const v = this.slerp(route.a, route.b, f)
          const alt = 1 + 0.12 * Math.sin(Math.PI * f)
          const p = this.project(v, spin, alt)

          if (p.z <= 0) {
            if (drawing) { ctx.stroke(); drawing = false }
            continue
          }

          const alpha = (0.08 + p.z * 0.3).toFixed(3)
          if (!drawing) {
            ctx.beginPath()
            ctx.strokeStyle = `rgba(${tone}, ${alpha})`
            ctx.moveTo(p.x, p.y)
            drawing = true
          } else {
            ctx.lineTo(p.x, p.y)
          }
        }
        if (drawing) ctx.stroke()

        // spedizione in transito, con scia
        const prog = (t * route.speed + route.offset) % 1
        for (let k = 0; k < 7; k++) {
          const f = prog - k * 0.016
          if (f < 0) continue

          const v = this.slerp(route.a, route.b, f)
          const alt = 1 + 0.12 * Math.sin(Math.PI * f)
          const p = this.project(v, spin, alt)
          if (p.z <= 0) continue

          const fade = (1 - k / 7) * (0.35 + p.z * 0.65)
          ctx.fillStyle = `rgba(${tone}, ${fade.toFixed(3)})`
          ctx.beginPath()
          ctx.arc(p.x, p.y, k === 0 ? 2.6 : 1.6 * (1 - k / 9), 0, Math.PI * 2)
          ctx.fill()
        }
      }

      // porti: punto fisso piu anello che pulsa
      for (let i = 0; i < PORTS.length; i++) {
        const p = this.project(this.toVector(PORTS[i].lat, PORTS[i].lon), spin)
        if (p.z <= 0) continue

        const depth = 0.3 + p.z * 0.7
        const lam = this.lambert(p, sun)
        ctx.fillStyle = `rgba(255, 214, 130, ${((0.35 + lam * 0.5) * depth).toFixed(3)})`
        ctx.beginPath()
        ctx.arc(p.x, p.y, 2.2, 0, Math.PI * 2)
        ctx.fill()

        const pulse = (t * 0.45 + i * 0.37) % 1
        ctx.strokeStyle = `rgba(255, 214, 130, ${((1 - pulse) * 0.4 * depth).toFixed(3)})`
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.arc(p.x, p.y, 2.2 + pulse * 16, 0, Math.PI * 2)
        ctx.stroke()
      }
    },

    // ---------- Eventi ----------
    onResize() {
      this.resizeCanvas()
      this.buildStars()
      if (this.reducedMotion) this.draw(0)
    },

    onVisibilityChange() {
      if (document.hidden) {
        this.stopLoop()
      } else if (!this.reducedMotion) {
        this.startLoop()
      }
    },

    // ---------- Autenticazione ----------
    async login() {
      this.loading = true
      this.error = null

      try {
        const authStore = useAuthStore()
        const result = await authStore.login(this.credentials)

        if (result.success) {
          // Reindirizza alla home dopo login riuscito
          this.$router.push('/')
        } else {
          // Gestisce diversi tipi di errore con traduzioni
          if (result.error?.includes('Database connection error')) {
            this.error = this.$t('login.errors.databaseError')
          } else if (result.error?.includes('Invalid credentials')) {
            this.error = this.$t('login.errors.invalidCredentials')
          } else if (result.error?.includes('password not configured')) {
            this.error = this.$t('login.errors.passwordNotConfigured')
          } else {
            this.error = result.error || this.$t('login.errors.unknownError')
          }
        }

      } catch (error) {
        this.error = this.$t('login.errors.serverError')
        console.error('Login failed:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  position: fixed;
  inset: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #04080f;
}

/* ---------- Sfondo ---------- */
.globe-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(120% 90% at 50% 0%, #0a1526 0%, #060d1a 55%, #04080f 100%);
}

.globe-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

/* Alone atmosferico sopra il limbo del pianeta */
.atmo {
  position: absolute;
  left: 50%;
  bottom: -18%;
  width: 130vmin;
  height: 130vmin;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(circle at 50% 100%, rgba(90, 160, 255, 0.20) 0%, rgba(90, 160, 255, 0.06) 42%, transparent 62%);
  filter: blur(30px);
  pointer-events: none;
  animation: atmo-breathe 14s ease-in-out infinite;
}

/* Scurisce il centro: la card resta leggibile */
.haze {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 50% 45%, rgba(4, 9, 18, 0.72) 0%, rgba(4, 9, 18, 0.35) 40%, transparent 70%),
    radial-gradient(ellipse at center, transparent 45%, rgba(3, 7, 14, 0.8) 100%);
}

@keyframes atmo-breathe {
  0%, 100% { opacity: 0.75; }
  50%      { opacity: 1; }
}

/* ---------- Card ---------- */
.login-content {
  position: relative;
  z-index: 1;
  width: 100%;
  padding: 24px;
  display: flex;
  justify-content: center;
}

.login-card {
  width: 100%;
  max-width: 420px;
  padding: 40px 36px 24px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow:
    0 40px 90px rgba(0, 0, 0, 0.65),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
  animation: card-in 0.6s ease-out both;
}

@keyframes card-in {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: none; }
}

.login-brand {
  text-align: center;
  margin-bottom: 28px;
}

.login-logo {
  max-width: 210px;
  max-height: 90px;
  width: auto;
  height: auto;
}

.login-subtitle {
  margin-top: 10px;
  font-size: 0.875rem;
  letter-spacing: 0.04em;
  color: #5b6478;
}

.login-form :deep(.v-field) {
  background: #f7f9fc;
}

.login-btn {
  margin-top: 4px;
  color: #1b2233;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: none;
  background-image: linear-gradient(135deg, #ffd875 0%, #f5b301 55%, #e39a00 100%);
  box-shadow: 0 10px 24px rgba(245, 179, 1, 0.35);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(245, 179, 1, 0.45);
}

.login-footer {
  margin-top: 22px;
  text-align: center;
  font-size: 0.75rem;
  color: #9aa2b1;
}

@media (max-width: 480px) {
  .login-card {
    padding: 32px 22px 20px;
    border-radius: 20px;
  }
}

/* Rispetta le preferenze di movimento ridotto */
@media (prefers-reduced-motion: reduce) {
  .atmo,
  .login-card {
    animation: none;
  }
}
</style>
